import { useMemo, type MutableRefObject } from 'react';
import type { LoupeDeLaCarte } from '../partage/loupeDeLaCarte';
import type { Bulle, Etat, Feuille } from '../partage/api/types';
import {
  VueTableau,
  type PoseDesPapiers,
  type RegroupementDeLaCarte,
  type useCalque,
  type ProfondeurIci,
} from '../carte';
import { VueHierarchie, type useHierarchisation } from '../hierarchie';
import { Maintenant } from '../maintenant';
import { Agenda } from '../agenda';
import { ArbreDuProjet } from './ArbreDuProjet';
import { DetailDUneBranche } from './DetailDUneBranche';
import { formeDessinee, habitsDessines, traitDessine } from './habit';
import type { Rangement } from '../partage/vocabulaire';
import { VueListe } from './VueListe';
import type { Vue } from './BasculeVue';
import type { Marge } from './modes';

/** La classe de la marge, posée sur le contenu pour le pousser (`MargesDeLaCarte.css`). */
const COTE_DE_LA_MARGE: Record<Marge, string> = { avancement: 'g', repertoire: 'd', frise: 'h' };

/**
 * L'aiguillage des angles d'une feuille (sorti de `Feuille.tsx`, chantier nettoyage-feuille,
 * ticket 02) : la Carte, la Liste, la Hiérarchie, Maintenant, l'Agenda, et le mot de la feuille
 * vide. **Aucune des trois premières n'est la vraie** : rien ne doit être visible dans l'une et
 * absent de l'autre. Agenda et Maintenant (chantier angles-du-projet, tickets 03 et 02) montrent
 * autre chose : ce qui est daté, ce que « Maintenant » propose, limités à la bulle ouverte, par
 * l'écran de la page lui-même.
 */
export function VuesDeLaFeuille({
  donnees,
  vue,
  auPouce,
  montrerCeQuiEstFait,
  rangement,
  onRanger,
  ficheOuverte,
  hierarchisation,
  enHierarchisation,
  onRecentrer,
  profondeur,
  calque,
  venantDe,
  onEntrer,
  onFermerLaFiche,
  onFiche,
  onSaFiche,
  onFicheDUnAngle,
  onEtat,
  onRond,
  loupe,
  poseDesPapiers,
  marges,
  panneauDAnalyse = false,
  onClicEnAnalyse = null,
  regroupe = null,
  liensCachesMontres = false,
  lier = null,
  onFicheDAilleurs,
  cadenasOuvert = false,
}: {
  donnees: Feuille;
  vue: Vue;
  auPouce: boolean;
  /** Au téléphone, « Montrer ce qui est fait », du ⚙ du projet (chantier telephone-projet, 02). */
  montrerCeQuiEstFait: boolean;
  rangement: Rangement;
  onRanger: (rangement: Rangement) => void;
  /** La bulle dont la fiche est ouverte : sa ligne se surligne. */
  ficheOuverte: string | null;
  hierarchisation: ReturnType<typeof useHierarchisation>;
  /** « Hiérarchiser » en cours, sur l'ordinateur : la Liste se désigne. */
  enHierarchisation: boolean;
  /** La Carte donne son geste « Recentrer » à la feuille, pour le menu de Profondeur. */
  onRecentrer: (recentrer: (() => void) | null) => void;
  profondeur: ProfondeurIci;
  calque: ReturnType<typeof useCalque>;
  /** D'où l'on vient de remonter : la carte teinte cette branche-là. */
  venantDe: string | null;
  /** Entrer dans une bulle : sa feuille. */
  onEntrer: (id: string) => void;
  onFermerLaFiche: () => void;
  /** Ouvrir une fiche sur place : une bulle de la feuille, ou plus bas. */
  onFiche: (id: string) => void;
  /** Au téléphone, la fiche de la bulle ouverte (l'intercalaire du détail d'une branche). */
  onSaFiche: (id: string) => void;
  /** Une ligne de l'angle Agenda ou Maintenant : sa fiche, sur place quand elle est sous la feuille. */
  onFicheDUnAngle: (id: string) => void;
  onEtat:(id: string, etat: Etat) => void;
  onRond: (bulle: Bulle) => void;
  /** Les touches de la carte (chantier modes-de-la-carte, modification 02), en Carte et en Hiérarchie. */
  loupe: LoupeDeLaCarte;
  /** P et L : la Carte y range ses gestes « + Post-it » et « + Liste ». */
  poseDesPapiers: MutableRefObject<PoseDesPapiers | null>;
  /** Les marges ouvertes (modes-de-la-carte, 03) : la carte se pousse pour leur faire place. */
  marges: ReadonlySet<Marge>;
  /** Le panneau d'analyse ouvert (theme-analyse, ticket 02) : la carte se pousse, comme pour une marge. */
  panneauDAnalyse?: boolean;
  /**
   * En Analyse, sur la Carte et la Hiérarchie, un clic sur une bulle l'allume avec ses lignes du
   * panneau, au lieu d'y entrer (maquette `clic`) ; `null` : un autre mode, le clic entre.
   */
  onClicEnAnalyse?: ((id: string) => void) | null;
  /** En Analyse, sur la Carte : les colonnes de Regrouper (theme-analyse, ticket 03) ; `null` : aucune. */
  regroupe?: RegroupementDeLaCarte | null;
  /** La touche A : les liens cachés se montrent sur la carte (chantier liens-entre-bulles, 03). */
  liensCachesMontres?: boolean;
  /** « Lier » en cours, sur la Carte (chantier liens-entre-bulles, ticket 04) ; `null` : non. */
  lier?: { premiere: string | null; onClic: (id: string) => void } | null;
  /** L'étiquette d'un lien vers une bulle d'ailleurs, sur la Carte : sa fiche par-dessus (liens-entre-bulles, 05). */
  onFicheDAilleurs?: (id: string) => void;
  /** Le cadenas de la Carte ouvert : une bulle se glisse et garde sa place (deplacer-les-bulles, 03). */
  cadenasOuvert?: boolean;
}) {
  const { bulle, enfants, couleur, carte } = donnees;
  /* La forme des bulles du projet, sur sa Carte et sa Hiérarchie (habit-du-projet, ticket 03). */
  const forme = formeDessinee(donnees.habit.forme, auPouce);
  /* Et le trait de leur contour (ticket 04). */
  const trait = traitDessine(donnees.habit.trait, auPouce);
  /* L'habit de chaque bulle, par-dessus celui du projet (habit-des-bulles, modification 03). */
  const habitDe = useMemo(() => habitsDessines(donnees, auPouce), [donnees, auPouce]);
  /* Le menu « ⋯ » d'une bulle en part (habit-des-bulles, modification 05) ; aucun au téléphone. */
  const habitDuProjet = useMemo(
    () => (auPouce ? null : { couleur, forme: donnees.habit.forme, trait: donnees.habit.trait }),
    [auPouce, couleur, donnees.habit.forme, donnees.habit.trait]
  );

  return (
    <>
      <div
        className={[
          `feuille-contenu feuille-contenu--${vue}`,
          ...[...marges].map((m) => `feuille-contenu--marge-${COTE_DE_LA_MARGE[m]}`),
          ...(panneauDAnalyse ? ['feuille-contenu--marge-a'] : []),
        ].join(' ')}
      >
        {auPouce && donnees.chemin.length > 1 ? (
          /* Au téléphone, plus bas que le projet, le détail de la branche (chantier telephone-projet, 03). */
          <DetailDUneBranche
            key={bulle.id}
            donnees={donnees}
            montrerCeQuiEstFait={montrerCeQuiEstFait}
            onMaillon={onEntrer}
            onEntrer={onEntrer}
            onFiche={onFiche}
            onSaFiche={() => onSaFiche(bulle.id)}
            onRond={onRond}
          />
        ) : auPouce ? (
          /* Au téléphone, l'arborescence (chantier telephone-projet, 02) : un morceau à part. */
          <ArbreDuProjet
            key={bulle.id}
            donnees={donnees}
            montrerCeQuiEstFait={montrerCeQuiEstFait}
            onEntrer={onEntrer}
            onFiche={onFiche}
          />
        ) : enfants.length === 0 && vue === 'liste' ? null : vue === 'maintenant' ? (
          <Maintenant key={bulle.id} angle={{ portee: bulle.id, onOuvrir: onFicheDUnAngle }} />
        ) : vue === 'agenda' ? (
          <Agenda key={bulle.id} angle={{ portee: bulle.id, onOuvrir: onFicheDUnAngle }} />
        ) : vue === 'hierarchie' ? (
          <VueHierarchie
            key={bulle.id}
            centre={bulle}
            enfants={enfants}
            ordre={donnees.ordreHierarchique}
            axes={carte.axes}
            calque={carte.calque}
            enAvant={calque.enAvant}
            fond={donnees.habit.fond}
            forme={forme}
            trait={trait}
            habitDe={habitDe}
            habitDuProjet={habitDuProjet}
            hierarchisation={hierarchisation.rangs}
            profondeur={profondeur}
            onEntrer={onClicEnAnalyse ?? onEntrer}
            onFiche={onFiche}
            onEtat={onEtat}
            onDesigner={hierarchisation.designer}
            loupe={loupe}
          />
        ) : vue === 'liste' ? (
          <VueListe
            key={bulle.id}
            centreId={bulle.id}
            enfants={enfants}
            ordre={donnees.ordreHierarchique}
            rangement={rangement}
            onRanger={onRanger}
            ficheOuverte={ficheOuverte}
            hierarchisation={enHierarchisation ? hierarchisation.rangs : null}
            onEntrer={(i) => {
              onFermerLaFiche();
              onEntrer(i);
            }}
            onFiche={onFiche}
            auPouce={auPouce}
            onRond={onRond}
            onDesigner={hierarchisation.designer}
          />
        ) : (
          <VueTableau
            key={bulle.id}
            centre={bulle}
            enfants={enfants}
            carte={carte}
            enAvant={calque.enAvant}
            onFiltrer={calque.choisir}
            decompte={bulle.decompte}
            onRecentrer={onRecentrer}
            couleur={couleur}
            fond={donnees.habit.fond}
            forme={forme}
            trait={trait}
            habitDe={habitDe}
            habitDuProjet={habitDuProjet}
            hierarchisation={hierarchisation.rangs}
            ouverteId={venantDe}
            profondeur={profondeur}
            onDescendre={(i) => (hierarchisation.actif ? hierarchisation.designer(i) : (onClicEnAnalyse ?? onEntrer)(i))}
            onFiche={onFiche}
            onEtat={onEtat}
            loupe={loupe}
            poseDesPapiers={poseDesPapiers}
            regroupe={regroupe}
            liensCachesMontres={liensCachesMontres}
            lier={lier}
            onFicheDAilleurs={onFicheDAilleurs}
            cadenasOuvert={cadenasOuvert}
          />
        )}
      </div>

      {/* À l'ordinateur, une feuille vide ne montre ni phrase ni bulle fictive (retour d'Igor du 2026-10-20). */}
    </>
  );
}
